import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getProjectByProjectId } from '../../services/projectService';
import type { Project } from '../../types/project';
import { ProjectTabNav } from './ProjectTabNav';
import { LocationsTabContent } from './LocationsTabContent';
import { WorkOrdersTabContent } from './WorkOrdersTabContent';
import { TestsTabContent } from './TestsTabContent';

export const ProjectDetailPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(false);
  const activeTab: 'locations' | 'workorders' | 'tests' =
    location.pathname.includes('/tests') ? 'tests'
    : location.pathname.includes('/workorders') ? 'workorders'
    : 'locations';

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const proj = await getProjectByProjectId(projectId);
        if (!cancelled) setProject(proj);
      } catch (err) {
        console.error('Failed to load project:', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Project "{projectId}" not found. It may have been deleted or the link is invalid.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Project: {project.ProjectName}
      </Typography>

      <ProjectTabNav
        projectId={projectId || ''}
        projectName={project.ProjectName}
        activeTab={activeTab}
      />

      {activeTab === 'locations' && (
        <LocationsTabContent project={project} projectId={projectId!} />
      )}
      {activeTab === 'workorders' && (
        <WorkOrdersTabContent projectKey={project.ProjectKey} projectId={projectId!} />
      )}
      {activeTab === 'tests' && (
        <TestsTabContent projectKey={project.ProjectKey} projectId={projectId} />
      )}
    </Box>
  );
};
